import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import * as PR from "../../prime-modules/index";
import AdminFooter from "../layout/admin-footer";
import AdminHeader from "../layout/admin-header";
import { useSelector, useDispatch } from 'react-redux';
import { 
    getSubscriptionPlans, 
    uploadSubscriptionPlanContractDocuments, 
    downloadSubscriptionPlanContractDocument, 
    deleteSubscriptionPlanContractDocuments,
    createSubscriptionPlan,
    getDeviceTypes,
    getSubscriptionBundles
} from "../../services/api.jsx";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PERMISSIONS, hasPermission } from "../../utils/permissions";
import { formatPriceCurrency } from "../../utils";
import { checkDataSize, filterCountryName } from "../../utils/reuse.js";
import { globalConfig } from "../../GlobalConfig";
import "./SubscriptionPlans.scss";

const emptyContractFiles = {
    contractInformation: null,
    contractSummary: null,
};

const currencyOptions = [
   { label: "GBP (£)", value: "GBP" },
    { label: "USD ($)", value: "USD" },
    { label: "EUR (€)", value: "EUR" },
    { label: "CAD ($)", value: "CAD" },
    { label: "AUD ($)", value: "AUD" },
    { label: "SGD ($)", value: "SGD" },
    { label: "CHF (Fr)", value: "CHF" },
    { label: "JPY (¥)", value: "JPY" },
    { label: "NZD ($)", value: "NZD" },
    { label: "AED (د.إ)", value: "AED" },
];

const defaultCurrencyOption = currencyOptions.find(
    (c) => c.value === process.env.REACT_APP_DEFAULT_CURRENCY ||
        c.value === globalConfig?.defaultCurrency) || currencyOptions[0];
const defaultCurrencyCode = defaultCurrencyOption?.value;
const SubscriptionPlans = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);
    const adminData = useSelector(state => state.adminAuth.adminSessionData);
    const headers = useMemo(() => {
        return { sessionid: adminData.sessionId };
    }, [adminData.sessionId]);

    const appName = process.env?.REACT_APP_NAME;
    const isData2GoIot = appName === 'data2go-iot';

    // Enable create subscription plan for Super Admin only
    const canManageSubs = hasPermission(adminData?.permission, PERMISSIONS.MANAGE_ADMINS) || adminData?.role === 1;

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [documentDialogVisible, setDocumentDialogVisible] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [documentFiles, setDocumentFiles] = useState(emptyContractFiles);
    const [uploadingDocuments, setUploadingDocuments] = useState(false);
    const [downloadingDocument, setDownloadingDocument] = useState({
        contractInformation: false,
        contractSummary: false,
    });
    const [deletingPlan, setDeletingPlan] = useState(null);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);

    // Create Subscription Plan state
    const [createPlanDialogVisible, setCreatePlanDialogVisible] = useState(false);
    const [submittingPlan, setSubmittingPlan] = useState(false);
    const [deviceTypesList, setDeviceTypesList] = useState([]);
    const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(false);
    const [subscriptionBundlesList, setSubscriptionBundlesList] = useState([]);
    const [loadingSubscriptionBundles, setLoadingSubscriptionBundles] = useState(false);

    // Fetch subscription plans and merge contract-documents list
    const fetchSubscriptionPlans = useCallback(() => {
        setLoading(true);
        getSubscriptionPlans(headers, dispatch, (response) => {
            setLoading(false);
            if (response.result === "SUCCESS") {
                setPlans(response.data || []);
            }
        });
    }, [dispatch, headers]);

    // Fetch device types from GET /v1/device-types API
    const fetchDeviceTypes = useCallback(() => {
        setLoadingDeviceTypes(true);
        getDeviceTypes(headers, dispatch, (response) => {
            setLoadingDeviceTypes(false);
            if (response.result === "SUCCESS") {
                setDeviceTypesList(Array.isArray(response.data) ? response.data : []);
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: response.error?.errorMsg || "Failed to fetch device types",
                });
            }
        });
    }, [dispatch, headers]);

    // Fetch subscription bundles from GET /v1/subscription-bundles API
    const fetchSubscriptionBundles = useCallback(() => {
        setLoadingSubscriptionBundles(true);
        getSubscriptionBundles(headers, dispatch, (response) => {
            setLoadingSubscriptionBundles(false);
            if (response.result === "SUCCESS") {
                setSubscriptionBundlesList(Array.isArray(response.data) ? response.data : []);
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: response.error?.errorMsg || "Failed to fetch subscription bundles",
                });
            }
        });
    }, [dispatch, headers]);

    // Device types options for MultiSelect
    const deviceTypeOptions = useMemo(() => {
        return deviceTypesList.map((dt) => {
            const id = Number(dt.id);
            const name = dt.name || `Device ${id}`;
            const label = dt.groupId ? `${name} (${dt.groupId})` : name;
            return {
                label,
                value: id,
            };
        });
    }, [deviceTypesList]);

    // Subscription bundles options for Dropdown
    const bundleOptions = useMemo(() => {
        return (subscriptionBundlesList || []).map((bundle) => {
            const id = Number(bundle.id);
            const name = bundle.name || `Bundle ${id}`;
            const dataInfo = bundle.dataAmountForDisplay || (bundle.dataAmount ? checkDataSize(bundle.dataAmount) : "");
            const durationInfo = bundle.duration
                ? `${bundle.duration} ${bundle.duration_unit ? bundle.duration_unit.toLowerCase() : "day"}${bundle.duration > 1 ? "s" : ""}`
                : "";
            const details = [dataInfo, durationInfo].filter(Boolean).join(", ");
            const label = details ? `${name} (${details})` : name;
            return {
                label,
                value: id,
                bundle,
            };
        });
    }, [subscriptionBundlesList]);

    const bundleOptionTemplate = (option) => {
        if (!option) return null;
        const b = option.bundle;
        if (!b) return <span>{option.label}</span>;
        const dataInfo = b.dataAmountForDisplay || (b.dataAmount ? checkDataSize(b.dataAmount) : "");
        const durationInfo = b.duration
            ? `${b.duration} ${b.duration_unit ? b.duration_unit.toLowerCase() : "day"}${b.duration > 1 ? "s" : ""}`
            : "";
        return (
            <div className="flex flex-column gap-1 py-1">
                <div className="font-semibold text-sm">
                    {b.name || `Bundle ${b.id}`}
                </div>
                {(dataInfo || durationInfo) && (
                    <div className="text-xs text-500 flex align-items-center gap-2">
                        {dataInfo && <span>Data: {dataInfo}</span>}
                        {dataInfo && durationInfo && <span>•</span>}
                        {durationInfo && <span>Duration: {durationInfo}</span>}
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        fetchSubscriptionPlans();
    }, [fetchSubscriptionPlans]);

    useEffect(() => {
        if (adminData?.sessionId) {
            fetchDeviceTypes();
            fetchSubscriptionBundles();
        }
    }, [adminData?.sessionId, fetchDeviceTypes, fetchSubscriptionBundles]);

    const handleCreateSubscriptionPlan = (values) => {
        if (!canManageSubs) return;
        const payload = {
            bundleID: Number(values.bundleID),
            deviceTypes: values.deviceTypes.map((id) => Number(id)),
            duration: Number(values.duration),
            freeBundleID: 0,
            planDescription: String(values.planDescription).trim(),
            planName: String(values.planName).trim(),
            planPrice: Number(values.planPrice),
            planPriceCurrency: String(values.planPriceCurrency || defaultCurrencyCode).trim(),
        };

        setSubmittingPlan(true);
        createSubscriptionPlan(payload, headers, dispatch, (response) => {
            setSubmittingPlan(false);
            if (response.result === "SUCCESS") {
                setCreatePlanDialogVisible(false);
                formik.resetForm();
                fetchSubscriptionPlans();
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Subscription plan created successfully.",
                    life: 3000,
                });
            } else {
                const error = response.error;
                const detail = error?.errorMsg || error?.summary || "Failed to create subscription plan.";
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail,
                    life: 4000,
                });
            }
        });
    };

    const formik = useFormik({
        initialValues: {
            planName: "",
            planDescription: "",
            planPrice: null,
            planPriceCurrency: defaultCurrencyCode,
            duration: null,
            bundleID: null,
            deviceTypes: [],
        },
        validationSchema: Yup.object({
            planName: Yup.string()
                .trim()
                .required("Plan name is required"),
            planDescription: Yup.string()
                .trim()
                .required("Plan description is required"),
            planPrice: Yup.number()
                .typeError("Plan price must be a valid number")
                .min(0, "Price must be greater than or equal to 0")
                .required("Plan price is required"),
            planPriceCurrency: Yup.string()
                .trim()
                .required("Currency is required"),
            duration: Yup.number()
                .typeError("Duration must be an integer")
                .integer("Duration must be a whole number")
                .min(1, "Duration must be at least 1")
                .required("Duration is required"),
            bundleID: Yup.number()
                .typeError("Please select a bundle")
                .min(0, "Bundle ID must be non-negative")
                .required("Bundle ID is required"),
            deviceTypes: Yup.array()
                .of(Yup.number().integer())
                .min(1, "Please select at least one device type")
                .required("At least one device type is required"),
        }),
        onSubmit: (values) => {
            handleCreateSubscriptionPlan(values);
        },
    });

    const handleOpenCreateDialog = () => {
        if (!canManageSubs) return;
        formik.resetForm();
        setCreatePlanDialogVisible(true);
        if (deviceTypesList.length === 0) {
            fetchDeviceTypes();
        }
        if (subscriptionBundlesList.length === 0) {
            fetchSubscriptionBundles();
        }
    };

    const handleCloseCreateDialog = () => {
        setCreatePlanDialogVisible(false);
        formik.resetForm();
    };


    const handleOpenDocumentDialog = (plan) => {
        if (isData2GoIot) return;
        setSelectedPlan(plan);
        setDocumentFiles(emptyContractFiles);
        setDocumentDialogVisible(true);
    };

    const contractInfoInputRef = React.useRef(null);
    const contractSummaryInputRef = React.useRef(null);

    const handleDocumentFileChange = (event, field) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        setDocumentFiles((prev) => ({ ...prev, [field]: file }));
        try { event.target.value = ''; } catch (e) {}
    };

    const openFilePicker = (field) => {
        if (field === 'contractInformation') {
            contractInfoInputRef.current?.click();
        } else {
            contractSummaryInputRef.current?.click();
        }
    };

    const handleUploadDocuments = () => {
        if (!selectedPlan || !selectedPlan.name) {
            return;
        }

        const formData = new FormData();
        if (documentFiles.contractInformation) {
            formData.append('contractInformation', documentFiles.contractInformation);
        }
        if (documentFiles.contractSummary) {
            formData.append('contractSummary', documentFiles.contractSummary);
        }

        setUploadingDocuments(true);
        uploadSubscriptionPlanContractDocuments(selectedPlan.name, formData, headers, dispatch, (response) => {
            setUploadingDocuments(false);
            if (response.result === 'SUCCESS') {
                setDocumentDialogVisible(false);
                setDocumentFiles(emptyContractFiles);
                fetchSubscriptionPlans();
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Contract documents uploaded successfully.',
                });
            } else {
                const error = response.error;
                const detail = error?.errorMsg || error?.summary || 'Unable to upload contract documents.';
                toast.current?.show({ severity: 'error', summary: 'Error', detail });
            }
        });
    };

    const triggerDownload = (plan, documentType) => {
        if (!plan || !plan.name) {
            return;
        }

        setDownloadingDocument((prev) => ({ ...prev, [documentType]: true }));
        // map internal documentType keys to API docType enum
        const docTypeMap = {
            contractInformation: 'contract-information',
            contractSummary: 'contract-summary',
        };
        const apiDocType = docTypeMap[documentType] || documentType;
        downloadSubscriptionPlanContractDocument(plan.name, apiDocType, headers, dispatch, (response) => {
            setDownloadingDocument((prev) => ({ ...prev, [documentType]: false }));
            if (response.result !== 'SUCCESS') {
                const error = response.error;
                const detail = error?.errorMsg || error?.summary || 'Unable to download contract document.';
                toast.current?.show({ severity: 'error', summary: 'Error', detail });
                return;
            }

            const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const disposition = response.headers?.['content-disposition'] || '';
            const fileName = disposition.split('filename=')[1]?.replace(/['"]/g, '') || `${plan.name}-${documentType}.pdf`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        });
    };

    const handleDeleteDocuments = (plan) => {
        if (!plan || !plan.name) return;
        setPlanToDelete(plan);
        setDeleteDialogVisible(true);
    };

    const confirmDeleteDocuments = () => {
        if (!planToDelete || !planToDelete.name) return;
        setDeletingPlan(planToDelete.name);
        setDeleteDialogVisible(false);
        deleteSubscriptionPlanContractDocuments(planToDelete.name, headers, dispatch, (response) => {
            setDeletingPlan(null);
            setPlanToDelete(null);
            if (response.result === 'SUCCESS') {
                fetchSubscriptionPlans();
                toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Contract documents deleted.' });
            } else {
                const error = response.error;
                const detail = error?.errorMsg || error?.summary || 'Unable to delete contract documents.';
                toast.current?.show({ severity: 'error', summary: 'Error', detail });
            }
        });
    };

    // Columns formatters
    const setDataAmount = (rowData) => {
        return rowData.dataAmountForDisplay ? rowData.dataAmountForDisplay : checkDataSize(rowData.dataAmount);
    };

    const setDataDuration = (rowData) => {
        if (!rowData.duration) return "-";
        const unit = rowData.duration_unit ? rowData.duration_unit.toLowerCase() : "day";
        const plural = rowData.duration > 1 ? "s" : "";
        return `${rowData.duration} ${unit}${plural}`;
    };

    const priceBodyTemplate = (rowData) => {
        return (
            <span>{formatPriceCurrency(rowData?.priceCurrency, rowData?.price)}</span>
        );
    };

    const providerCodeBodyTemplate = (rowData) => {
        const providerMap = {
            1: 'eSIM-Go',
            2: 'Airalo',
            3: 'Cobira',
            4: 'Uk Domestic'
        };
        return (
            <span>{providerMap[rowData.providerCode] || rowData.providerCode || "-"}</span>
        );
    };

    const displayCountry = (rowData) => {
        const countries = rowData.countries || [];
        if (countries.length === 0) {
            return rowData.iso ? rowData.iso : "-";
        }
        return countries
            .map((c) => filterCountryName(c.country?.name))
            .filter(Boolean)
            .join(", ") || "-";
    };

    const contractDocumentsTemplate = (rowData) => {
        const hasContractDocuments = !!rowData?.hasContractDocuments;

        return (
            <div className="contract-documents-cell">
                {!hasContractDocuments && (
                    <div className="contract-documents-actions">
                        <PR.Button
                            label={'Upload Documents'}
                            icon="pi pi-upload"
                            size="small"
                            severity={'secondary'}
                            onClick={() => handleOpenDocumentDialog(rowData)}
                            className="upload-btn"
                        />
                    </div>
                )}
                {hasContractDocuments && (
                    <div className="doc-tiles">
                        <div className="doc-tile">
                            <PR.Button
                                icon="pi pi-download"
                                className={`doc-tile-button download `}
                                aria-label="Contract Information"
                                onClick={() => triggerDownload(rowData, 'contractInformation')}
                            />
                            <div className="doc-tile-label">{downloadingDocument.contractInformation ? 'Downloading...' : 'Information'}</div>
                        </div>

                        <div className="doc-tile">
                            <PR.Button
                                icon="pi pi-download"
                                className={`doc-tile-button summary`}
                                aria-label="ContractSummary"
                                onClick={() => triggerDownload(rowData, 'contractSummary')}
                            />
                            <div className="doc-tile-label">{downloadingDocument.contractSummary ? 'Downloading...' : 'Summary'}</div>
                        </div>

                        <div className="doc-tile" onClick={() => handleOpenDocumentDialog(rowData)} style={{ cursor: 'pointer' }}>
                            <PR.Button
                                icon="pi pi-upload"
                                className="doc-tile-button upload"
                                aria-label="Upload Documents"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDocumentDialog(rowData);
                                }}
                            />
                            <div className="doc-tile-label">Edit Documents</div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="main">
                <div className="layout-sidebar">
                    <AdminHeader />
                </div>
                <div className="layout-content-wrapper">
                    <section className="admin-users-section price-adjust-section subscription-plans-section">
                        <div className="grid grid-nogutter">
                            <div className="col-12">
                                <div className="heading-sec mb-4 mobile-header-flex">
                                    <div className="flex align-items-center justify-content-between mobile-flex">
                                        <h1>Subscription Plans</h1>
                                        <div className="right-section flex align-items-center gap-3">
                                            {canManageSubs && (
                                                <PR.Button
                                                    label="Create Subscription Plan"
                                                    icon="pi pi-plus"
                                                    className="create-button"
                                                    onClick={handleOpenCreateDialog}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="users-data-table price-adjustment subscription-plans-table card mt-4">
                            <PR.DataTable
                                loading={loading}
                                value={plans}
                                className="price-adjustment-table"
                                emptyMessage={<span className="text-gray-500">No results found</span>}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 20, 50]}
                                paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                                paginatorClassName="select-bundle-pagination"
                                sortField="dataAmount"
                                sortOrder={1}
                            >
                                <PR.Column field="providerCode" header="Provider Code" body={providerCodeBodyTemplate} style={{ width: "18%" }} sortable />
                                <PR.Column 
                                    body={(rowData) => (
                                        <div>
                                            <div>{rowData.description || "-"}</div>
                                            {rowData.duration && (
                                                <div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>
                                                    {setDataDuration(rowData)}
                                                </div>
                                            )}
                                        </div>
                                    )}  
                                    header="Plan" 
                                    field="description" 
                                    style={{ width: "18%" }} 
                                    sortable 
                                />
                                <PR.Column 
                                    body={(rowData) => (
                                        <div>
                                            <div>{rowData.name || "-"}</div>
                                            {rowData.groups && (
                                                <div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>
                                                    {rowData.groups}
                                                </div>
                                            )}
                                        </div>
                                    )}  
                                    header="Bundle Name" 
                                    field="name" 
                                    style={{ width: "20%" }} 
                                    sortable 
                                />
                                <PR.Column field="dataAmount" header="Data" style={{ width: "11%" }} body={setDataAmount} sortable />
                                <PR.Column field="price" header="Retail Price" style={{ width: "17%" }} body={priceBodyTemplate} sortable />
                               {/*  <PR.Column field="bundleType" header="Bundle Type" style={{ width: "15%" }} sortable /> */}
                                <PR.Column header="Country" body={displayCountry} style={{ width: "20%" }} />
                                {!isData2GoIot && (
                                    <PR.Column header="Contract Documents" body={contractDocumentsTemplate} style={{ width: "30%" }} />
                                )}
                            </PR.DataTable>
                        </div>
                    </section>

                    <PR.Dialog
                        visible={!isData2GoIot && documentDialogVisible}
                        dismissableMask={true}
                        draggable={false}
                        resizable={false}
                        header={selectedPlan ? `${selectedPlan.name || selectedPlan.description || 'Plan'} Contract Documents` : 'Contract Documents'}
                        onHide={() => { setDocumentDialogVisible(false); setDocumentFiles(emptyContractFiles); }}
                        className="affiliate-dialog"
                        maskClassName="affiliate-dialog-mask"
                        style={{ width: '90%', maxWidth: '600px' }}
                    >
                        <div style={{ marginTop: '1rem' }} className="onboard-dialog-content">
                            <div className="flex flex-column gap-2 mb-3">
                                <p className="m-0 font-semibold text-sm">
                                    Contract Information PDF {!selectedPlan?.hasContractDocuments && <span className="text-red-500">*</span>}
                                </p>
                                <div className="flex gap-2">
                                    <PR.InputText
                                        value={documentFiles.contractInformation ? documentFiles.contractInformation.name : ''}
                                        placeholder="No file chosen"
                                        className="flex-1"
                                        readOnly
                                    />
                                    <input
                                        ref={contractInfoInputRef}
                                        type="file"
                                        accept="application/pdf"
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleDocumentFileChange(e, 'contractInformation')}
                                    />
                                    <PR.Button
                                        label="Choose file"
                                        onClick={() => openFilePicker('contractInformation')}
                                        className="confirm-button"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-column gap-2 mb-3">
                                <p className="m-0 font-semibold text-sm">
                                    Contract Summary PDF {!selectedPlan?.hasContractDocuments && <span className="text-red-500">*</span>}
                                </p>
                                <div className="flex gap-2">
                                    <PR.InputText
                                        value={documentFiles.contractSummary ? documentFiles.contractSummary.name : ''}
                                        placeholder="No file chosen"
                                        className="flex-1"
                                        readOnly
                                    />
                                    <input
                                        ref={contractSummaryInputRef}
                                        type="file"
                                        accept="application/pdf"
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleDocumentFileChange(e, 'contractSummary')}
                                    />
                                    <PR.Button
                                        label="Choose file"
                                        onClick={() => openFilePicker('contractSummary')}
                                        className="confirm-button"
                                    />
                                </div>
                            </div>

                            <div className="dialog-footer buttons-sections flex gap-3 align-items-center justify-content-end mt-5 p-0">
                                <PR.Button
                                    label="Cancel"
                                    type="button"
                                    onClick={() => setDocumentDialogVisible(false)}
                                    className="confirm-button reset-btn min-width"
                                    disabled={uploadingDocuments}
                                />
                                <PR.Button
                                    label={uploadingDocuments ? 'Submitting...' : 'Submit'}
                                    onClick={handleUploadDocuments}
                                    className="confirm-button min-width"
                                    disabled={uploadingDocuments || (!documentFiles.contractInformation && !documentFiles.contractSummary)}
                                />
                            </div>
                        </div>
                    </PR.Dialog>

                    <PR.Dialog
                        visible={deleteDialogVisible}
                        header="Confirm Deletion"
                        className="delete-kyc-dialog"
                        draggable={false}
                        resizable={false}
                        modal
                        onHide={() => setDeleteDialogVisible(false)}
                    >
                        <div className="confirmation-content flex align-items-center">
                            <i className="pi pi-exclamation-triangle mr-3 text-red-500" style={{ fontSize: "2rem" }} />
                            {planToDelete && (
                                <span>
                                    Are you sure you want to delete contract documents for <b>{planToDelete.name || planToDelete.description || planToDelete}</b>?
                                </span>
                            )}
                        </div>
                        <div className="flex justify-content-end mt-4 buttons-sections gap-2">
                            <PR.Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text confirm-button reset-btn"
                                onClick={() => setDeleteDialogVisible(false)}
                                disabled={!!deletingPlan}
                            />
                            <PR.Button
                                label="Delete"
                                icon="pi pi-trash"
                                severity="danger"
                                className="confirm-button yes"
                                onClick={confirmDeleteDocuments}
                                loading={!!deletingPlan}
                            />
                        </div>
                    </PR.Dialog>

                    <PR.Dialog
                        visible={canManageSubs && createPlanDialogVisible}
                        dismissableMask={false}
                        draggable={false}
                        resizable={false}
                        header="Create Subscription Plan"
                        onHide={handleCloseCreateDialog}
                        className="affiliate-dialog create-subscription-plan-dialog"
                        maskClassName="affiliate-dialog-mask"
                        style={{ width: "92%", maxWidth: "680px" }}
                    >
                        <form onSubmit={formik.handleSubmit} className="onboard-dialog-content pt-2">
                            <div className="grid">
                                {/* Plan Name */}
                                <div className="col-12 md:col-6 flex flex-column gap-2 mb-3">
                                    <label htmlFor="planName" className="font-semibold text-sm">
                                        Plan Name <span className="text-red-500">*</span>
                                    </label>
                                    <PR.InputText
                                        id="planName"
                                        name="planName"
                                        placeholder="e.g. monthly_5"
                                        value={formik.values.planName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={formik.errors.planName && formik.touched.planName ? "p-invalid" : ""}
                                    />
                                    {formik.errors.planName && formik.touched.planName && (
                                        <small className="p-error">{formik.errors.planName}</small>
                                    )}
                                </div>

                                {/* Plan Description */}
                                <div className="col-12 md:col-6 flex flex-column gap-2 mb-2">
                                    <label htmlFor="planDescription" className="font-semibold text-sm">
                                        Plan Description <span className="text-red-500">*</span>
                                    </label>
                                    <PR.InputText
                                        id="planDescription"
                                        name="planDescription"
                                        placeholder="e.g. Monthly 5GB"
                                        value={formik.values.planDescription}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={formik.errors.planDescription && formik.touched.planDescription ? "p-invalid" : ""}
                                    />
                                    {formik.errors.planDescription && formik.touched.planDescription && (
                                        <small className="p-error">{formik.errors.planDescription}</small>
                                    )}
                                </div>

                                {/* Plan Price */}
                                <div className="col-12 md:col-6 flex flex-column gap-2 mb-2">
                                    <label htmlFor="planPrice" className="font-semibold text-sm">
                                        Plan Price <span className="text-red-500">*</span>
                                    </label>
                                    <PR.InputNumber
                                        id="planPrice"
                                        name="planPrice"
                                        value={formik.values.planPrice}
                                        onValueChange={(e) => formik.setFieldValue("planPrice", e.value)}
                                        onBlur={formik.handleBlur}
                                        mode="decimal"
                                        minFractionDigits={2}
                                        maxFractionDigits={2}
                                        min={0}
                                        placeholder="0.00"
                                        className={formik.errors.planPrice && formik.touched.planPrice ? "p-invalid w-full" : "w-full"}
                                    />
                                    {formik.errors.planPrice && formik.touched.planPrice && (
                                        <small className="p-error">{formik.errors.planPrice}</small>
                                    )}
                                </div>

                                {/* Currency */}
                                <div className="col-12 md:col-6 flex flex-column gap-2 mb-2">
                                    <label htmlFor="planPriceCurrency" className="font-semibold text-sm">
                                        Currency <span className="text-red-500">*</span>
                                    </label>
                                    <PR.Dropdown
                                        id="planPriceCurrency"
                                        name="planPriceCurrency"
                                        value={formik.values.planPriceCurrency}
                                        options={[defaultCurrencyOption]}
                                        disabled
                                        readOnly
                                        placeholder="Select Currency"
                                        className={formik.errors.planPriceCurrency && formik.touched.planPriceCurrency ? "p-invalid w-full" : "w-full"}
                                    />
                                    {formik.errors.planPriceCurrency && formik.touched.planPriceCurrency && (
                                        <small className="p-error">{formik.errors.planPriceCurrency}</small>
                                    )}
                                </div>

                                {/* Duration */}
                                <div className="col-12 md:col-6 flex flex-column gap-2 mb-2">
                                    <label htmlFor="duration" className="font-semibold text-sm">
                                        Duration <span className="text-red-500">*</span>
                                    </label>
                                    <PR.InputNumber
                                        id="duration"
                                        name="duration"
                                        value={formik.values.duration}
                                        onValueChange={(e) => formik.setFieldValue("duration", e.value)}
                                        onBlur={formik.handleBlur}
                                        min={1}
                                        useGrouping={false}
                                        className={formik.errors.duration && formik.touched.duration ? "p-invalid w-full" : "w-full"}
                                    />
                                    {formik.errors.duration && formik.touched.duration && (
                                        <small className="p-error">{formik.errors.duration}</small>
                                    )}
                                </div>

                                {/* Bundle ID */}
                                <div className="col-12 md:col-6 flex flex-column gap-2 mb-2">
                                    <div className="flex align-items-center justify-content-between">
                                        <label htmlFor="bundleID" className="font-semibold text-sm">
                                            Bundle ID <span className="text-red-500">*</span>
                                        </label>
                                    </div>
                                    <PR.Dropdown
                                        id="bundleID"
                                        name="bundleID"
                                        value={formik.values.bundleID}
                                        options={bundleOptions}
                                        onChange={(e) => formik.setFieldValue("bundleID", e.value)}
                                        onBlur={formik.handleBlur}
                                        placeholder={loadingSubscriptionBundles ? "Loading bundles..." : "Select Bundle"}
                                        filter
                                        showClear
                                        filterBy="label"
                                        itemTemplate={bundleOptionTemplate}
                                        className={formik.errors.bundleID && formik.touched.bundleID ? "p-invalid w-full" : "w-full"}
                                        emptyMessage={loadingSubscriptionBundles ? "Loading..." : "No bundles found"}
                                        disabled={loadingSubscriptionBundles}
                                    />
                                    {formik.errors.bundleID && formik.touched.bundleID && (
                                        <small className="p-error">{formik.errors.bundleID}</small>
                                    )}
                                </div>


                                {/* Device Types */}
                                <div className="col-12 flex flex-column gap-2 mb-2">
                                    <div className="flex align-items-center justify-content-between">
                                        <label htmlFor="deviceTypes" className="font-semibold text-sm">
                                            Device Types <span className="text-red-500">*</span>
                                        </label>
                                    </div>
                                    <PR.MultiSelect
                                        id="deviceTypes"
                                        name="deviceTypes"
                                        value={formik.values.deviceTypes}
                                        options={deviceTypeOptions}
                                        onChange={(e) => formik.setFieldValue("deviceTypes", e.value)}
                                        onBlur={formik.handleBlur}
                                        placeholder={loadingDeviceTypes ? "Loading device types..." : "Select Device Types"}
                                        display="chip"
                                        filter
                                        className={formik.errors.deviceTypes && formik.touched.deviceTypes ? "p-invalid w-full" : "w-full"}
                                        emptyMessage={loadingDeviceTypes ? "Loading..." : "No device types found"}
                                    />
                                    {formik.errors.deviceTypes && formik.touched.deviceTypes ? (
                                        <small className="p-error">{formik.errors.deviceTypes}</small>
                                    ) : (
                                        <small className="text-500">&nbsp;</small>
                                    )}
                                </div>
                            </div>

                            <div className="dialog-footer buttons-sections flex gap-3 align-items-center justify-content-end mt-4 p-0">
                                <PR.Button
                                    label="Cancel"
                                    type="button"
                                    onClick={handleCloseCreateDialog}
                                    className="confirm-button reset-btn min-width"
                                    disabled={submittingPlan}
                                />
                                <PR.Button
                                    label={submittingPlan ? "Creating..." : "Create Plan"}
                                    type="submit"
                                    className="confirm-button min-width"
                                    loading={submittingPlan}
                                />
                            </div>
                        </form>
                    </PR.Dialog>

                    <PR.Toast ref={toast} position="top-right" />

                    <AdminFooter />
                </div>
            </div>
        </>
    );
};

export default SubscriptionPlans;
